import { Box, Card, Typography, Stack } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import GalleryIcon from '../../../../assets/images/icons/gallery.svg';
import LikeIcon from '../../../../assets/images/icons/like.svg';
import PostIcon from '../../../../assets/images/icons/post.svg';
import EngagementRateIcon from '../../../../assets/images/icons/engagement_rate.svg';
import { ToolContext } from '../../../../core/context/ToolContext';
import { SnakeLoader } from '../../../../core/loader/SnakeLoader';
import { fetchAllInstagramPosts, fetchAllPosts, fetchFacebookInsights, fetchUserAuthDetails, PostDataProcessing, fetchAllLinkedInPosts } from '../helpers/metricHelpers';
import { rqx } from '../../../../core/request/API';
import { env } from '../../../../core/Env';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const SocialMediaEngagement = () => {
    const { social_media_insight_state, tool_state } = useContext(ToolContext);
    const { userId, __SESSION } = tool_state;
    const insightData = social_media_insight_state.insightData.data;
    const platform = social_media_insight_state.connectedPlatform.data;

    const listView = async () => {
        if (!(platform !== '' && !social_media_insight_state.showProfilesModal.data &&
            ((platform !== 'facebook' && platform !== 'linkedin') ||
                (platform === 'facebook' && social_media_insight_state.accessToken.data) ||
                (platform?.toLowerCase() === 'linkedin' && social_media_insight_state.linkedInAccessToken.data)))) return;
        try {
            social_media_insight_state.insightLoading.set(true);
            social_media_insight_state.topContentsLoading.set(true);
            let posts = [];
            let followersCount = 0;

            if (platform === 'facebook') {
                const facebookInitialUrl = `${social_media_insight_state.selectedProfileId.data}/posts?fields=id,created_time,message,full_picture,insights.metric(post_media_view),comments{created_time,message},likes.summary(true).limit(0),shares&limit=100`;
                const { allPosts } = await fetchAllPosts(facebookInitialUrl, social_media_insight_state.accessToken.data);
                posts = allPosts;

                // Fetch Facebook page follows
                const followsResponse = await rqx.fbGraph(
                    `${social_media_insight_state.selectedProfileId.data}/?fields=followers_count`,
                    social_media_insight_state.accessToken.data
                );
                // Get the latest follows count from the response
                if (followsResponse) {
                    followersCount = followsResponse.followers_count;
                }
            } else if (platform === 'instagram') {
                const userAuthDetails = await fetchUserAuthDetails(__SESSION.data.uaccess[0].company_id, userId.data, '2');
                const instagramInitialUrl = `${userAuthDetails.account_id}/media?fields=id,caption,media_url,timestamp,like_count,comments_count,media_type,username,children{id,media_url,timestamp,like_count,comments_count,media_type,username}&limit=100`;
                const { allPosts } = await fetchAllInstagramPosts(instagramInitialUrl, userAuthDetails.access_token);
                posts = allPosts;

                // Fetch Instagram followers count
                const followersResponse = await rqx.lgGraph(
                    `${userAuthDetails.account_id}?fields=followers_count`,
                    userAuthDetails.access_token
                );
                followersCount = followersResponse?.followers_count || 0;
            } else if (platform?.toLowerCase() === 'linkedin') {
                const userAuthDetails = await fetchUserAuthDetails(__SESSION.data.uaccess[0].company_id, userId.data, '3');
                if (userAuthDetails) {
                    const organizationId = social_media_insight_state.selectedProfileId.data || userAuthDetails.page_id;

                    if (organizationId) {
                        const { allPosts: linkedInPosts } = await fetchAllLinkedInPosts(
                            organizationId,
                            __SESSION.data.uaccess[0].company_id,
                            userId.data
                        );

                        // posts = transformLinkedInPostsToStandardFormat(linkedInPosts);
                        posts = linkedInPosts;
                    }
                }

                const response = await rqx.get(`<<tool>>/${env()}/linkedInInsights/get_linkedIn_followers_count`, { user_id: userId.data });
                followersCount = response.data.followers || 0;
            }

            const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
            const endDate = dayjs().format('YYYY-MM-DD');
            let viewsData = [];
            if (platform == 'facebook') {
                const data = await fetchFacebookInsights({
                    profileId: social_media_insight_state.selectedProfileId.data,
                    accessToken: social_media_insight_state.accessToken.data,
                    startDate: startDate,
                    endDate: endDate,
                });
                viewsData = data?.data[0].values;
            }
            //  else if (platform == 'instagram') {
            //     const data = await fetchInstagramViews(social_media_insight_state.instagramAccountId.data, social_media_insight_state.instagramAccessToken.data, startDate, endDate);
            //     if(!data) return;
            //     viewsData = Object.entries(data).map(([date, value]) => ({
            //         value: value,
            //         end_time: date
            //     }));
            // }
            const responseData = PostDataProcessing(posts, viewsData);
            // Add followers count to response data
            responseData.total_followers = followersCount;
            social_media_insight_state.insightData.set(responseData);

            const topPosts = posts.map(post => ({
                views: post.insights?.data?.[0]?.values?.[0]?.value || post.post_engagement || 0,
                likes: post.likes?.summary?.total_count || post.like_count || 0,
                comments: post.comments?.data?.length || post.comments_count || 0,
                message: post.message || post.caption || '',
                post_picture: post.full_picture || post.media_url || ''
            }));
            topPosts.sort((a, b) => b.views - a.views);
            social_media_insight_state.topContentsData.set(topPosts);
        } catch (error) {
            console.error('Error fetching social media insights:', error);
            social_media_insight_state.insightData.set([]);
            social_media_insight_state.topContentsData.set([]);
        } finally {
            social_media_insight_state.insightLoading.set(false);
            social_media_insight_state.topContentsLoading.set(false);
        }
    };

    useEffect(() => {
        listView()
    }, [social_media_insight_state.urlCode.data, platform]);

    const data = insightData || {};

    const metrics = [
        {
            icon: <img src={GalleryIcon} alt="Post Add" />,
            color: "#3074DD",
            value: data.total_posts || 0,
            label: 'Total Post',
            change: `${data.total_posts_change_pct > 0 ? '+' : ''}${data.total_posts_change_pct || 0}%`,
            iconBg: '#DDEAFF',
            changeText: 'than last week'
        },
        {
            icon: <img src={LikeIcon} alt="Post Add" />,
            color: "#F2263A",
            value: data.total_likes || 0,
            label: 'Total Likes',
            change: `${data.total_likes_change_pct > 0 ? '+' : ''}${data.total_likes_change_pct || 0}%`,
            iconBg: '#FDEBED',
            changeText: 'than last week'
        },
        {
            icon: <img src={PostIcon} alt="Post Add" />,
            color: "#E77634",
            value: data.avg_likes_per_post || 0,
            label: 'Average Likes Per Post',
            change: `${data.avg_likes_change_pct > 0 ? '+' : ''}${data.avg_likes_change_pct || 0}%`,
            iconBg: '#FFEEE5',
            changeText: 'than last week'
        },
        {
            icon: <img src={EngagementRateIcon} alt="Post Add" />,
            color: "#948800",
            value: data.total_followers || 0,
            label: 'Total Followers',
            change: '0%',
            iconBg: '#FFFCD7',
            changeText: 'than last week'
        }
    ];

    return (
        <Box marginBottom="28px">
            <Box fontSize={20} fontWeight={600} mb='24px'>
                Engagement, Likes and Post Report
            </Box>
            <Box
                display="grid"
                gap='21px'
                width="100%"
                gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))"
            >
                {metrics.map((metric, index) => (
                    <Card
                        key={index}
                        elevation={0}
                        variant="outlined"
                        sx={{ border: 'none', borderRadius: '20px' }}
                        className='social-media-engagement-card'
                    >
                        <Box p="18px">
                            <Stack spacing={0} >
                                <Box display="flex" gap={1.8} alignItems="center">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        width={40}
                                        height={40}
                                        bgcolor={metric.iconBg}
                                        borderRadius="8px"
                                        border='none'
                                        color={metric.color}
                                    >
                                        {metric.icon}
                                    </Box>
                                    {(social_media_insight_state.insightLoading.data || social_media_insight_state.topContentsLoading.data) ?
                                        <Box sx={{ display: 'flex', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                                            <SnakeLoader size="6px" bg="#11783C" distance="0.1rem" />
                                        </ Box>
                                        :
                                        <Typography
                                            variant="h4"
                                            color="#106C36"
                                            fontSize={28}
                                            fontWeight={700}
                                        >
                                            {metric.value}
                                        </Typography>
                                    }
                                </Box>
                                <Box sx={{ marginTop: "10px" }}>
                                    <Typography
                                        mb="7px"
                                        fontWeight={600}
                                        fontSize={16}
                                    >
                                        {metric.label}
                                    </Typography>
                                    <Box display='flex' gap="4px">
                                        <Typography
                                            variant="caption"
                                            color="#377E36"
                                            display="block"
                                            fontSize="10px"
                                            padding='3px'
                                            borderRadius='3px'
                                            sx={{ background: '#E0F0E4' }}
                                        >
                                            {metric.change}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="#9797A9"
                                            display="block"
                                            fontSize="10px"
                                            padding='3px'
                                            borderRadius='3px'
                                        >
                                            {metric.changeText}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};